import { styled } from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Outlet, useParams } from 'react-router';
import { SurveyEditorSkeletonLoader } from '../../components/SkeletonLoader/SurveyEditor.SkeletonLoader';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { fetchSurveyById, SURVEYS, useUpdateSurvey } from '../../queries/useSurveys';
import { useTemplateSections } from '../../queries/useTemplates';
import {
  activeQuestionIdState,
  activeSectionIdState,
  activeSurveyIdState,
  activeSurveyState,
  areShowedAllQuestionsState,
  isNameModalOpenState,
  responsesState,
  sectionsState,
  survayNameState,
  surveyTemplatesDataState,
} from '../../state/UIState';
import { EditSurveyArgs, SurveyTemplateSection } from '../../types';
import { ProgressHeader } from './components/ProgressHeader';
import { EditSurveyModal } from '../../components/EditSurveyModal/EditSurveyModal';

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const Content = styled('div')``;

export const SurveyEditor = () => {
  const [isSurveyLoading, setSurveyIsLoading] = useState(true);
  const [surveyName, setSurveyName] = useAtom(survayNameState);
  const [open, setOpen] = useAtom(isNameModalOpenState);
  const [activeSurveyId, setActiveSurveyId] = useAtom(activeSurveyIdState);
  const [activeSurvey, setActiveSurvey] = useAtom(activeSurveyState);
  const [, setActiveSectionId] = useAtom(activeSectionIdState);
  const [, setActiveQuestionId] = useAtom(activeQuestionIdState);
  const [sections, setSections] = useAtom(sectionsState);
  const setResponses = useSetAtom(responsesState);
  const setAreShowedAllQuestions = useSetAtom(areShowedAllQuestionsState);
  const handleClose = useCallback(() => setOpen(false), [setOpen]);
  const updateSurvey = useUpdateSurvey();
  const queryClient = useQueryClient();
  const params = useParams();
  const { isLoading: areTemplateSectionsLoading, data: templateSections } = useTemplateSections();
  const isLoading = isSurveyLoading || areTemplateSectionsLoading;
  const [, setSurveyTemplatesData] = useAtom(surveyTemplatesDataState);

  const debouncedSections = useDebouncedValue(sections, 500);

  const handleConfirm = useCallback(
    async ({
      newName,
      description,
      includeEvaluation,
      includeWeight,
      includeQuestionNumbers,
      evaluationRules,
      evaluationScale,
    }: EditSurveyArgs) => {
      handleClose();
      setSurveyName(newName);
      if (!activeSurveyId) return;

      await updateSurvey.mutateAsync(
        {
          id: activeSurveyId,
          name: newName,
          description,
          evaluationRules,
          evaluationScale,
          includeEvaluation,
          includeWeight,
          includeQuestionNumbers,
        },
        {
          onSuccess: (newSuvey) => {
            setActiveSurvey(newSuvey);
          },
        }
      );
      queryClient.refetchQueries(SURVEYS);
    },
    [activeSurveyId, handleClose, queryClient, setActiveSurvey, setSurveyName, updateSurvey]
  );

  useEffect(() => {
    async function fetchSurveyData() {
      let numberOfAttemps = 0;
      const maxNumberOfAttemps = 5;
      async function getFirstValid() {
        try {
          const survey = await fetchSurveyById(Number(params.id));
          setSurveyName(survey?.name ?? '');
          setSections(survey?.sections ?? []);
          setActiveSurveyId(survey?.id ?? null);
          setActiveSurvey(survey ?? null);
          setActiveSectionId(survey?.sections[0]?.id ?? null);
          setActiveQuestionId(survey?.sections[0]?.questions[0]?.id ?? null);
          setSurveyIsLoading(false);
        } catch (error) {
          console.log({ error });
          numberOfAttemps++;
          if (numberOfAttemps < maxNumberOfAttemps) {
            getFirstValid();
          }
        }
      }
      if (params.id) {
        getFirstValid();
      }
    }
    fetchSurveyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  useEffect(() => {
    if (activeSurveyId) {
      updateSurvey.mutate({ id: activeSurveyId, sections: debouncedSections });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSections]);

  useEffect(() => {
    if (!templateSections) return;
    //create array of categories where each category has an array of sections
    const categories = templateSections?.reduce((acc, section) => {
      if (!section.category) {
        const uncategorized = acc.find((cat) => cat.name === 'Uncategorized');
        if (uncategorized) {
          uncategorized.sections.push(section);
        } else {
          acc.push({ name: 'Uncategorized', sections: [section] });
        }
        return acc;
      }
      const category = acc.find((cat) => cat.name === section.category?.name);
      if (category) {
        category.sections.push(section);
      } else {
        acc.push({ name: section.category?.name, sections: [section] });
      }
      return acc;
    }, [] as { name: string; sections: SurveyTemplateSection[] }[]);
    setSurveyTemplatesData(categories);
  }, [setSurveyTemplatesData, templateSections]);

  useEffect(() => {
    return () => {
      setActiveSurveyId(null);
      setActiveSectionId(null);
      setActiveQuestionId(null);
      setSections([]);
      setResponses({});
      setAreShowedAllQuestions(true);
      setSurveyName('');
      setActiveSurvey(null);
      setOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCancel = useCallback(() => {
    handleClose();
  }, [handleClose]);

  if (isLoading) return <SurveyEditorSkeletonLoader />;

  return (
    <Wrapper>
      <ProgressHeader />
      <Content>
        <Outlet />
      </Content>
      <EditSurveyModal
        onClose={onCancel}
        isOpen={open}
        title={'Edit Survey Settings'}
        surveyName={surveyName}
        onConfirm={handleConfirm}
        confirmText={'Save Changes'}
        activeSurvey={activeSurvey}
      />
    </Wrapper>
  );
};
